import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query/react';

import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { apiSlice } from './api';
import authSliceReducer from '../features/authSlice';
import mainSlice from '../features/mainSlice';
import modalReducer from '../features/modalSlice';
import libraryReducer from '../features/librarySlice';
import shareSlice from '../features/shareSlice';
import driveSlice from '../features/driveSlice';
import searchSlice from '../features/searchSlice';
import trashSlice from '../features/trashSlice';
import infoSlice from '../features/infoSlice';
import uploadSlice from '../features/uploadSlice';
import classroomSlice from '../features/classroomSlice';
import settingsSlice from '../features/settingsSlice';
import storageSlice from '../features/storageSlice';
import analysisSlice from '../features/analysisSlice';

const rootPersistConfig = {
  key: 'root',
  storage,
  blacklist: [
    apiSlice.reducerPath,
    'modal',
    'library',
    'share',
    'drive',
    'search',
    'trash',
    'settings',
    'classroom',
    'upload',
    'storage',
    'analysis',
  ],
};

const authPersistConfig = {
  key: 'auth',
  storage,
};
const mainPersistConfig = {
  key: 'main',
  storage,
};
const modalPersistConfig = {
  key: 'modal',
  storage,
};
const infoPersistConfig = {
  key: 'info',
  storage,
};
const uploadPersistConfig = {
  key: 'upload',
  storage,
};
const libraryPersistConfig = {
  key: 'library',
  storage,
  blacklist: ['page'],
};
const sharePersistConfig = {
  key: 'share',
  storage,
  blacklist: ['page'],
};
const drivePersistConfig = {
  key: 'drive',
  storage,
  blacklist: ['page'],
};
const searchPersistConfig = {
  key: 'search',
  storage,
  blacklist: ['page'],
};
const trashPersistConfig = {
  key: 'trash',
  storage,
  blacklist: ['page'],
};
const settingsPersistConfig = {
  key: 'settings',
  storage,
};
const classroomPersistConfig = {
  key: 'classroom',
  storage,
};
const storagePersistConfig = {
  key: 'storage',
  storage,
};
const analysisPersistConfig = {
  key: 'analysis',
  storage,
};

const rootReducer = combineReducers({
  [apiSlice.reducerPath]: apiSlice.reducer,
  auth: persistReducer(authPersistConfig, authSliceReducer),
  main: persistReducer(mainPersistConfig, mainSlice),
  modal: persistReducer(modalPersistConfig, modalReducer),
  info: persistReducer(infoPersistConfig, infoSlice),
  upload: persistReducer(uploadPersistConfig, uploadSlice),
  library: persistReducer(libraryPersistConfig, libraryReducer),
  share: persistReducer(sharePersistConfig, shareSlice),
  drive: persistReducer(drivePersistConfig, driveSlice),
  search: persistReducer(searchPersistConfig, searchSlice),
  trash: persistReducer(trashPersistConfig, trashSlice),
  settings: persistReducer(settingsPersistConfig, settingsSlice),
  classroom: persistReducer(classroomPersistConfig, classroomSlice),
  storage: persistReducer(storagePersistConfig, storageSlice),
  analysis: persistReducer(analysisPersistConfig, analysisSlice),
});

const persistedReducer = persistReducer(rootPersistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          'modal/modalToggled',
        ],
        ignoredPaths: ['modal.entities.confirmFileReplace.data.handler'],
      },
    }).concat(apiSlice.middleware),
  devTools: process.env.NODE_ENV !== 'prod',
});

setupListeners(store.dispatch);

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
